/**
 * SUPPORT TICKET NOTIFICATION SERVICE
 * 
 * Handles email notifications for support ticket workflow:
 * - New ticket notifications to support team
 * - Response notifications to users
 */

import { Resend } from 'resend';
import { 
 NewTicketNotificationTemplate, 
 TicketResponseNotificationTemplate,
 NewTicketNotificationData,
 TicketResponseNotificationData 
} from '../email/templates/SupportTicketEmailTemplate';
// API proxy helper - all DB operations go through server-side API
async function callSupportTicketNotifyAPI(operation: string, params: any = {}): Promise<any> {
 const response = await fetch('/api/user/support-ticket-notify', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ operation, ...params })
 });
 return response.json();
}

// Environment configuration
const RESEND_API_KEY = (import.meta as any).env.VITE_RESEND_API_KEY || process.env.RESEND_API_KEY;
const RESEND_FROM_EMAIL = (import.meta as any).env.VITE_RESEND_FROM_EMAIL || process.env.RESEND_FROM_EMAIL || 'verify@aureus.africa';
const RESEND_FROM_NAME = (import.meta as any).env.VITE_RESEND_FROM_NAME || process.env.RESEND_FROM_NAME || 'Aureus Alliance Holdings';
const SUPPORT_EMAIL = 'support@aureus.africa';
const WEBSITE_URL = 'https://aureus.africa';

// Initialize Resend client
let resend: Resend | null = null;

try {
 if (RESEND_API_KEY) {
 resend = new Resend(RESEND_API_KEY);
 console.log('Support ticket notification service initialized');
 } else {
 console.warn('RESEND_API_KEY not found - support ticket notifications disabled');
 }
} catch (error) {
 console.error('Failed to initialize support ticket notification service:', error);
}

// Initialize email templates
const newTicketTemplate = new NewTicketNotificationTemplate();
const responseTemplate = new TicketResponseNotificationTemplate();

export interface SupportTicketNotificationResult {
 success: boolean;
 messageId?: string;
 error?: string;
}

/**
 * Send new ticket notification to support team
 */
export async function sendNewTicketNotification(
 ticketData: {
 id: string;
 ticket_number: string;
 user_id: number;
 title: string;
 description: string;
 priority: 'low' | 'medium' | 'high' | 'urgent';
 category: string;
 user_type: 'shareholder' | 'affiliate';
 }
): Promise<SupportTicketNotificationResult> {
 if (!resend) {
 console.warn('Email service not available - skipping new ticket notification');
 return { success: false, error: 'Email service not configured' };
 }

 try {
 console.log('Sending new ticket notification for:', ticketData.ticket_number);

 // Get user information via API
 const userResult = await callSupportTicketNotifyAPI('get_user', { userId: ticketData.user_id });
 if (!userResult.success) {
 console.error('Error fetching user for ticket notification:', userResult.error);
 return { success: false, error: 'User not found' };
 }
 const userData = userResult.data as { id: number; username: string; full_name: string | null; email: string };

 // Prepare email data
 const emailData: NewTicketNotificationData = {
 email: userData.email,
 fullName: userData.full_name || userData.username || 'Unknown User',
 ticketNumber: ticketData.ticket_number,
 userInfo: {
 name: userData.full_name || userData.username || 'Unknown User',
 email: userData.email,
 userType: ticketData.user_type,
 userId: userData.id
 },
 ticket: {
 title: ticketData.title,
 description: ticketData.description,
 priority: ticketData.priority,
 category: ticketData.category
 },
 adminDashboardUrl: `${WEBSITE_URL}/admin/support/tickets/${ticketData.id}`
 };

 // Generate email content
 const template = newTicketTemplate.generateTemplate(emailData);
 const subject = template.subject;
 const htmlContent = template.htmlContent;
 const textContent = template.textContent;

 // Send email to support team
 const emailResult = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [SUPPORT_EMAIL],
 subject: subject,
 html: htmlContent,
 text: textContent,
 tags: [
 { name: 'category', value: 'support_ticket' },
 { name: 'type', value: 'new_ticket' },
 { name: 'priority', value: ticketData.priority },
 { name: 'user_type', value: ticketData.user_type }
 ]
 });

 const emailId = emailResult.data?.id || 'unknown';
 console.log('New ticket notification sent successfully:', emailId);

 // Log the notification
 await logNotification({
 ticket_id: ticketData.id,
 notification_type: 'new_ticket_to_support',
 recipient_email: SUPPORT_EMAIL,
 email_id: emailId,
 status: 'sent'
 });

 return { success: true, messageId: emailId };

 } catch (error) {
 console.error('Error sending new ticket notification:', error);
 
 // Log the failed notification
 await logNotification({
 ticket_id: ticketData.id,
 notification_type: 'new_ticket_to_support',
 recipient_email: SUPPORT_EMAIL,
 status: 'failed',
 error_message: error instanceof Error ? error.message : 'Unknown error'
 });

 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
}

/**
 * Send ticket response notification to user
 */
export async function sendTicketResponseNotification(
 ticketId: string,
 responseData: {
 statusUpdate?: string;
 responseMessage?: string;
 agentId?: string;
 }
): Promise<SupportTicketNotificationResult> {
 if (!resend) {
 console.warn('Email service not available - skipping response notification');
 return { success: false, error: 'Email service not configured' };
 }

 try {
 console.log('Sending ticket response notification for ticket:', ticketId);

 // Get ticket and user information via API
 const ticketResult = await callSupportTicketNotifyAPI('get_ticket_with_user', { ticketId });
 if (!ticketResult.success) {
 console.error('Error fetching ticket for response notification:', ticketResult.error);
 return { success: false, error: 'Ticket not found' };
 }
 const ticket = ticketResult.data;

 // Get agent information if provided
 let agentInfo = undefined;
 if (responseData.agentId) {
 const agentResult = await callSupportTicketNotifyAPI('get_agent', { agentId: responseData.agentId });
 if (agentResult.success && agentResult.data) {
 agentInfo = { name: agentResult.data.name, email: agentResult.data.email };
 }
 }

 // Type assertion for ticket data
 const ticketData = ticket as any;

 // Prepare email data
 const emailData: TicketResponseNotificationData = {
 email: ticketData.users.email,
 fullName: ticketData.users.full_name || ticketData.users.username || 'User',
 ticketNumber: ticketData.ticket_number,
 userInfo: {
 name: ticketData.users.full_name || ticketData.users.username || 'User',
 email: ticketData.users.email
 },
 ticket: {
 title: ticketData.title,
 statusUpdate: responseData.statusUpdate,
 responseMessage: responseData.responseMessage
 },
 agentInfo: agentInfo,
 dashboardUrl: `${WEBSITE_URL}/dashboard?section=messages&ticket=${ticketData.id}`
 };

 // Generate email content
 const template = responseTemplate.generateTemplate(emailData);
 const subject = template.subject;
 const htmlContent = template.htmlContent;
 const textContent = template.textContent;

 // Send email to user
 const emailResult = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [ticketData.users.email],
 subject: subject,
 html: htmlContent,
 text: textContent,
 tags: [
 { name: 'category', value: 'support_ticket' },
 { name: 'type', value: 'response_to_user' },
 { name: 'ticket_id', value: ticketId }
 ]
 });

 const emailId = emailResult.data?.id || 'unknown';
 console.log('Ticket response notification sent successfully:', emailId);

 // Log the notification
 await logNotification({
 ticket_id: ticketId,
 notification_type: 'response_to_user',
 recipient_email: ticketData.users.email,
 email_id: emailId,
 status: 'sent'
 });

 return { success: true, messageId: emailId };

 } catch (error) {
 console.error('Error sending ticket response notification:', error);
 
 // Log the failed notification
 await logNotification({
 ticket_id: ticketId,
 notification_type: 'response_to_user',
 status: 'failed',
 error_message: error instanceof Error ? error.message : 'Unknown error'
 });

 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
}

/**
 * Log notification attempts for debugging and tracking
 */
async function logNotification(logData: {
 ticket_id: string;
 notification_type: string;
 recipient_email?: string;
 email_id?: string;
 status: 'sent' | 'failed';
 error_message?: string;
}): Promise<void> {
 try {
 await callSupportTicketNotifyAPI('log_notification', { logData: {
 ticket_id: logData.ticket_id,
 notification_type: logData.notification_type,
 recipient_email: logData.recipient_email,
 email_id: logData.email_id,
 status: logData.status,
 error_message: logData.error_message,
 sent_at: new Date().toISOString()
 }});

 console.log('Notification logged successfully');
 } catch (error) {
 console.error('Error logging notification:', error);
 // Don't throw here - logging failure shouldn't break the main flow
 }
}

/**
 * Test email service configuration
 */
export async function testEmailService(): Promise<boolean> {
 if (!resend) {
 console.warn('Email service not configured');
 return false;
 }

 try {
 // Send a test email to support
 await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [SUPPORT_EMAIL],
 subject: ' Support Ticket Email Service Test',
 html: '<p>This is a test email to verify the support ticket notification service is working correctly.</p>',
 text: 'This is a test email to verify the support ticket notification service is working correctly.',
 tags: [{ name: 'category', value: 'test' }]
 });

 console.log('Email service test successful');
 return true;
 } catch (error) {
 console.error('Email service test failed:', error);
 return false;
 }
}
